import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Star, Download, Trash2, Expand } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { getAbsoluteUrl } from '../utils/urlHelper';


const API = "/api";

const FullscreenImageViewer = ({ 
  images, 
  currentIndex, 
  isOpen, 
  onClose, 
  onSetMainImage, 
  onDeleteImage,
  canManage = false 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const imgRef = useRef(null);

  useEffect(() => {
    setCurrentImageIndex(currentIndex);
  }, [currentIndex]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSetMainImage = async () => {
    if (!canManage) return;
    
    const currentImage = images[currentImageIndex];
    setLoading(true);
    
    try {
      await axios.put(
        `${API}/products/${currentImage.product_id}/images/${currentImage.id}/main`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      
      onSetMainImage(currentImage.id);
      toast({
        title: "Success",
        description: "Image set as main product image",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to set main image",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!canManage) return;
    
    const currentImage = images[currentImageIndex];
    setLoading(true);
    
    try {
      await axios.delete(
        `${API}/products/${currentImage.product_id}/images/${currentImage.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      
      onDeleteImage(currentImage.id);
      
      // Close viewer if this was the last image
      if (images.length === 1) {
        onClose();
      } else {
        // Move to next image or previous if this was the last one
        setCurrentImageIndex((prev) => 
          prev === images.length - 1 ? 0 : prev
        );
      }
      
      toast({
        title: "Success",
        description: "Image deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const currentImage = images[currentImageIndex];
    const link = document.createElement('a');
    link.href = getAbsoluteUrl(currentImage.image_url);
    link.download = currentImage.image_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFullscreen = () => {
    if (imgRef.current) {
      const imgElement = imgRef.current;
      if (imgElement.requestFullscreen) {
        imgElement.requestFullscreen();
      } else if (imgElement.webkitRequestFullscreen) {
        imgElement.webkitRequestFullscreen();
      } else if (imgElement.msRequestFullscreen) {
        imgElement.msRequestFullscreen();
      }
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentImageIndex] || images[0];

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      {/* Close button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:bg-white/20"
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        </>
      )}

      {/* Main image */}
      <div className="max-w-7xl max-h-full p-4 flex items-center justify-center">
        <img
          ref={imgRef}
          src={getAbsoluteUrl(currentImage.image_url)}
          alt={currentImage.image_name}
          className="max-w-full max-h-full object-contain"
        />
      </div>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium">
              {currentImage.image_name}
            </span>
            {currentImage.is_main && (
              <Badge variant="secondary" className="bg-yellow-500 text-white">
                <Star className="w-3 h-3 mr-1" />
                Main Image
              </Badge>
            )}
            <Badge variant="outline" className="text-white border-white/50">
              {currentImageIndex + 1} / {images.length}
            </Badge>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFullscreen}
              className="text-white hover:bg-white/20"
            >
              <Expand className="w-4 h-4 mr-1" />
              Fullscreen
            </Button>
            {canManage && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSetMainImage}
                  disabled={loading || currentImage.is_main}
                  className="text-white hover:bg-white/20"
                >
                  <Star className="w-4 h-4 mr-1" />
                  Set Main
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteImage}
                  disabled={loading}
                  className="text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="text-white hover:bg-white/20"
            >
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Image counter dots */}
      {images.length > 1 && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentImageIndex ? 'bg-white' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FullscreenImageViewer;